import { useState, useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';
import useFetchStateless from 'hooks/useFetch';
import { getActivities } from '../actions';
import { filterActivities } from '../utils';
const useActivities = (assetId, tradingAccountId, forceReloadConfig) => {
    const [state, setState] = useState({
        loading: true,
        error: false,
        data: null,
    });
    const initialLoading = useRef(true);
    const nextPageToken = useRef(undefined);
    const prevTradingAccountId = useRef(undefined);
    const [, , , fetch] = useFetchStateless();
    const onLoadNextPage = useCallback(async () => {
        if (tradingAccountId) {
            const token = nextPageToken.current;
            if (token) {
                setState((oldState) => ({ ...oldState, loading: true, error: false }));
                const params = {
                    paging: { token },
                    accountId: tradingAccountId,
                };
                try {
                    if (assetId)
                        params.assetIds = [assetId];
                    nextPageToken.current = undefined; // clear paging token to avoid extra requests for the same page
                    const response = await fetch(getActivities(params));
                    if (response?.activities) {
                        nextPageToken.current = response.paging.next;
                        setState((oldState) => ({
                            loading: false,
                            error: false,
                            data: _.uniqBy(oldState.data?.concat(response.activities.filter(filterActivities)) ??
                                response.activities.filter(filterActivities), (i) => i.id),
                        }));
                    }
                    else {
                        // treat missing activities as an error and restore the token so the user can retry
                        nextPageToken.current = token;
                        setState((oldState) => ({ ...oldState, loading: false, error: true }));
                    }
                }
                catch {
                    nextPageToken.current = token; // restore token so next-page retry is possible
                    initialLoading.current = false;
                    setState((oldState) => ({ ...oldState, loading: false, error: true }));
                }
            }
            else {
                // log('[onLoadNextPage] No paging token, skipping', false);
            }
        }
    }, [assetId, tradingAccountId]);
    const loadFirstPage = useCallback(async () => {
        if (tradingAccountId) {
            initialLoading.current = true;
            nextPageToken.current = undefined;
            setState((oldState) => ({ ...oldState, data: [], loading: true, error: false }));
            try {
                const params = { paging: { page: 1, perPage: 30 }, accountId: tradingAccountId };
                if (assetId)
                    params.assetIds = [assetId];
                const response = await fetch(getActivities(params));
                if (response?.activities) {
                    initialLoading.current = false;
                    nextPageToken.current = response.paging.next;
                    setState({
                        loading: false,
                        error: false,
                        data: _.uniqBy(response.activities.filter(filterActivities), (i) => i.id),
                    });
                }
                else {
                    // Modal.showError('Could not load activity.\nTry again a bit later.');
                    // pop();
                    initialLoading.current = false;
                    setState((oldState) => ({ ...oldState, loading: false, error: true }));
                }
            }
            catch {
                // Modal.showError('Could not load activity.\nTry again a bit later.');
                // pop();
                initialLoading.current = false;
                setState((oldState) => ({ ...oldState, loading: false, error: true }));
            }
        }
    }, [assetId, tradingAccountId]);
    useEffect(() => {
        if (tradingAccountId) {
            // Check if tradingAccountId has changed
            const hasAccountChanged = prevTradingAccountId.current !== tradingAccountId;
            if (hasAccountChanged) {
                // Reset state when account changes
                initialLoading.current = true;
                nextPageToken.current = undefined;
                prevTradingAccountId.current = tradingAccountId;
            }
            if (forceReloadConfig?.forceReload || initialLoading.current) {
                loadFirstPage();
            }
        }
        // this effect should only run once or when the object forceReloadConfig changes with forceReload as true.
        // the effect is designed to rerun when navigated with params forceReloadConfig: { forceReload: true } as
        // forceReloadConfig is an object and will change when new navigation happens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceReloadConfig, tradingAccountId]);
    return [state.data, onLoadNextPage, false, initialLoading.current && state.loading];
};
export default useActivities;
